import { useState } from 'react';
import type { NextPage } from 'next';
import Link from 'next/link';

import { Container, InputText } from '@dansk-metal/ui';

import { LinkListBlockType } from '@web/blocks/link-list-block/link-list-block';

import HeroTitle from '@web/components/hero-block/hero-title/hero-title';
import { LinkList } from '@web/components/link-list/link-list';

import TemplateLayout from '@web/layouts/template/template';

import { HeroAlignment } from '@web/services/umbraco/types/basic/Alignment';
import { BackgroundColor } from '@web/services/umbraco/types/basic/Color';
import { UmbracoPage } from '@web/services/umbraco/types/documents/UmbracoDocument';

import styles from './self-service-overview-page.module.scss';

export const selfServiceOverviewPageContentAlias = 'selfServiceOverviewPage' as const;

export type SelfServiceOverviewPageData = UmbracoPage<
	typeof selfServiceOverviewPageContentAlias,
	{
		title: string;
		subtitle?: string;
		sections: LinkListBlockType[];
	}
>;

const SelfServiceOverviewPageTemplate: NextPage<{ pageData: SelfServiceOverviewPageData }> = ({ pageData }) => {
	const { title, subtitle, sections } = pageData.properties;
	const [sectionsState, setSectionsState] = useState<LinkListBlockType[]>(sections);

	function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
		const searchValue = event.target.value.toLowerCase();

		const filteredSections = sections.map((section) => {
			const filteredPages = section.contentProperties.links.filter((linkItem) =>
				linkItem.name.toLowerCase().includes(searchValue),
			);

			return {
				...section,
				contentProperties: {
					...section.contentProperties,
					links: filteredPages,
				},
			} satisfies LinkListBlockType;
		});

		setSectionsState(filteredSections);
	}

	return (
		<TemplateLayout>
			{!!title && (
				<HeroTitle
					title={title}
					subtitle={subtitle}
					alignment={HeroAlignment.Left}
					background={BackgroundColor.White}
				/>
			)}
			<Container>
				<div className={styles.search_wrapper}>
					<InputText onChange={handleSearch} label='Find selvbetjeningsløsning' noOptionalLabel icon={{ icon: 'search', size: 24 }}/>
				</div>
				<div className={styles.sections}>
					{sectionsState.map((section) => {
						if (section.contentProperties.links.length === 0) return null;

						const links = section.contentProperties.links.map((linkItem) => (
							<Link href={linkItem.url} key={linkItem.key} >{linkItem.name}</Link>
						));

						return (
							<div key={section.key} className={styles.section_wrapper}>
								<LinkList title={section.contentProperties.title} links={links} />
							</div>
						);
					})}
				</div>
			</Container>
		</TemplateLayout>
	);
};

export default SelfServiceOverviewPageTemplate;
